/**
 * Driver auth store — tracks whether the driver has a usable JWT pair, and
 * which halves of this portal they should be shown.
 *
 * Deliberately minimal — JWT lifetime + refresh is handled by the axios
 * interceptor. This store is the single source of truth for "is the
 * driver logged in?" used by the router guard and the layout.
 *
 * `session` is the second thing it now holds. This app hosts two doors: the
 * rewards portal, whose launch is gated on a decision nobody has made, and the
 * vehicle-inspection door, which is deliberately independent of that gate. The
 * router has to know which halves exist before it lets a navigation through,
 * and the only honest answer comes from the server — a build-time constant
 * would go stale the moment the flag moved, and a driver meeting a rewards
 * page is the exact thing the separate DVIR door exists to prevent.
 */
import { defineStore } from 'pinia'
import {
  driverApi,
  dvirApi,
  tokenStorage,
  type DriverSession,
  type TokenPair,
  DRIVER_OIDC,
  oidcSession,
  oidcLogout,
  oidcCarriers,
  getDriverCarrierId,
  setDriverCarrierId,
  type DriverCarrier,
} from '@/services/api'

const SIGN_IN_REDIRECT_KEY = 'driver_portal_mechanic_redirect'

/**
 * A mechanic opened a job link signed out, the guard saved it as `?redirect=`,
 * and the sign-in email can only say `next=/mechanic`. So the sign-in page
 * parks the destination here and the verify page picks it up after the token
 * lands. In-app mechanic paths only: this value ends in `router.replace`, and
 * `redirect` arrives in a URL anyone can craft.
 */
export function safeMechanicRedirect(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (!/^\/mechanic\/[A-Za-z0-9._~\-/?=&%]+$/.test(value) || value.includes('//')) return null
  if (value.startsWith('/mechanic/sign-in')) return null
  const path = value.split('?')[0] ?? ''
  if (path.split('/').some((part) => part === '.' || part === '..') || /%2e|%2f|%5c/i.test(path)) return null
  return value
}

export function rememberSignInRedirect(value: unknown): void {
  const path = safeMechanicRedirect(value)
  try {
    if (path) localStorage.setItem(SIGN_IN_REDIRECT_KEY, path)
    else localStorage.removeItem(SIGN_IN_REDIRECT_KEY)
  } catch { /* Storage unavailable: the link lands on the home list. */ }
}

export function takeSignInRedirect(): string | null {
  try {
    const path = safeMechanicRedirect(localStorage.getItem(SIGN_IN_REDIRECT_KEY))
    localStorage.removeItem(SIGN_IN_REDIRECT_KEY)
    return path
  } catch { return null }
}

interface AuthState {
  employeeId: string | null
  initialized: boolean
  session: DriverSession | null
  sessionLoading: Promise<DriverSession | null> | null
  carriers: DriverCarrier[]
  carrierChooserOpen: boolean
}

export const useAuthStore = defineStore('driverAuth', {
  state: (): AuthState => ({
    employeeId: null,
    initialized: false,
    session: null,
    sessionLoading: null,
    carriers: [],
    carrierChooserOpen: false,
  }),

  getters: {
    isAuthenticated(state): boolean {
      return !!state.employeeId
    },
    /**
     * Whether the rewards half of this portal is open.
     *
     * Defaults to FALSE while unknown, on purpose. An unknown answer that
     * defaults open would flash a rewards page at a driver who arrived through
     * the compliance door — briefly, but that is the whole exposure.
     */
    rewardsEnabled(state): boolean {
      return state.session?.capabilities
        ? state.session.capabilities.rewards === true && state.session.capabilities.mechanic !== true && state.session.rewards_enabled === true
        : state.session?.rewards_enabled === true
    },
    mechanicEnabled(state): boolean {
      return state.session?.capabilities?.mechanic === true
    },
    driverEnabled(state): boolean {
      return state.session?.capabilities?.driver === true
    },
    driverFirstName(state): string | null {
      return state.session?.first_name ?? null
    },
  },

  actions: {
    initialize() {
      if (this.initialized) return
      this.employeeId = tokenStorage.getEmployeeId()
      this.initialized = true
    },

    async initializeOidc() {
      if (!DRIVER_OIDC || this.employeeId) return
      try {
        const session = await oidcSession()
        this.employeeId = session.person_id
        await this.loadCarriers()
      } catch {
        this.employeeId = null
      }
    },

    async loadCarriers() {
      if (!DRIVER_OIDC) return
      this.carriers = await oidcCarriers()
      const saved = getDriverCarrierId()
      if (this.carriers.length > 1 && saved &&
          this.carriers.some((carrier) => carrier.carrier_id === saved)) {
        this.carrierChooserOpen = false
      } else {
        setDriverCarrierId(null)
        this.carrierChooserOpen = this.carriers.length > 1
      }
    },

    chooseCarrier(carrierId: string) {
      if (!this.carriers.some((carrier) => carrier.carrier_id === carrierId)) return
      setDriverCarrierId(carrierId)
      this.carrierChooserOpen = false
    },

    async requireCarrierChoice() {
      if (!DRIVER_OIDC) return
      setDriverCarrierId(null)
      if (this.carriers.length < 2) {
        try { await this.loadCarriers() } catch { return }
      }
      this.carrierChooserOpen = this.carriers.length > 1
    },

    setSession(pair: TokenPair) {
      tokenStorage.set(pair)
      this.employeeId = pair.employee_id
      this.session = null
    },

    /**
     * Fetch the driver's session once and cache it.
     *
     * Single-flight: the router guard and the view that follows it both want
     * this, and they run back to back. Returns null rather than throwing so a
     * guard never strands a navigation on a network blip — the caller decides
     * what an unknown session means, and every caller here treats it as
     * "assume the smaller portal".
     */
    async ensureSession(): Promise<DriverSession | null> {
      if (this.session) return this.session
      if (this.sessionLoading) return this.sessionLoading

      this.sessionLoading = dvirApi
        .session(true)
        .then(async (s) => {
          // The neutral endpoint carries authority; keep the established
          // inspection greeting from its driver-only profile when applicable.
          if (s.capabilities?.driver && !s.capabilities.mechanic) {
            try {
              const profile = await dvirApi.session()
              s = { ...profile, ...s, capabilities: s.capabilities }
            } catch {
              // The capability answer is still usable without display names.
            }
          }
          this.session = s
          if (DRIVER_OIDC) this.employeeId = s.employee_id
          return s
        })
        .catch(() => null)
        .finally(() => {
          this.sessionLoading = null
        })

      return this.sessionLoading
    },

    async logout() {
      if (DRIVER_OIDC) {
        setDriverCarrierId(null)
        this.carriers = []
        this.carrierChooserOpen = false
        this.employeeId = null
        this.session = null
        try { await oidcLogout() } catch { /* Local state is already cleared. */ }
        return
      }
      const refresh = tokenStorage.getRefresh()
      if (refresh) {
        try {
          await driverApi.revoke(refresh)
        } catch {
          // Best-effort — even if the server can't be reached, clear local state.
        }
      }
      tokenStorage.clear()
      this.employeeId = null
      this.session = null
    },
  },
})
